/**
 * Which page a PHONE opens on.
 *
 * A column layout is the shape a narrow screen can actually read: one column of
 * cards, each sized by the card-size toggle, instead of a canvas that has to be
 * pinched. The hub therefore carries one page marked as the mobile default — a
 * normal dashboard row (`layoutType: 'column'`) whose `layoutConfig` says so —
 * and the landing guard sends a narrow screen there.
 *
 * The mark lives in the row's own JSON, the same zero-migration idiom the layout
 * marker and the BIM objects library use: no column, no migration, and a hub
 * that has no such page simply lands where it always did.
 */
import { wireObject } from './wire';

/** The key a dashboard's `layoutConfig` carries when it is the mobile default. */
export const MOBILE_DEFAULT_KEY = 'mobileDefault';

/** The smallest row this module reads: a dashboard's id and its config. */
export interface MobileDefaultCandidate {
  id: string;
  layoutConfig?: unknown;
}

/** Is this row the page a phone opens? */
export function isMobileDefaultDashboard(row: { layoutConfig?: unknown } | null | undefined): boolean {
  const config = wireObject(row?.layoutConfig);
  return config?.[MOBILE_DEFAULT_KEY] === true;
}

/**
 * The mobile default among `rows`, or undefined when none is marked.
 *
 * A marked row that is a LAYOUT is not a candidate: a layout is a canvas (a
 * plan, a diagram, the model), and a phone opening one would be the exact
 * pinch-to-read problem the column page exists to avoid.
 */
export function pickMobileDefault<T extends MobileDefaultCandidate>(
  rows: readonly T[],
  isLayout: (row: T) => boolean,
): T | undefined {
  return rows.find((row) => isMobileDefaultDashboard(row) && !isLayout(row));
}
